import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';
import { API_BASE_URL } from '@/env';
import { hardNavigate } from '@/app-base';
import { getDeviceId } from '@/auth/device-id';
import {
  clearPersistedSession,
  readRefreshToken,
  writeRefreshToken,
} from '@/auth/token-storage';
import { noteServerDate } from '@/lib/clock-skew';
import { IAM_BASE } from './paths';

/**
 * OTURUM TASIYICISI — src/lib/api.ts'teki MEVCUT axios ornegine takilir.
 *
 * Ayri bir ornek KURULMAZ: iki ornek olsaydi bazi cagrilar sessizce jetonsuz
 * giderdi ve hangisinin hangisi oldugu cagri yerinde gorunmezdi.
 */

/** Yeniden deneme isaretini tasiyan istek yapilandirmasi. */
type RetryableConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

/* -------------------------------------------------------------------------- */
/* Erisim jetonu — MODUL ICI degisken                                          */
/* -------------------------------------------------------------------------- */

/**
 * Erisim jetonu YALNIZ BURADA, YALNIZ BELLEKTE.
 *
 * Depodan (session.store) okunmuyor: bu dosya depoyu import etseydi
 * client -> store -> client dongusu olusurdu; Vite bunu cozer ama modullerden
 * biri yariya kadar baslatilmis halde otekine gorunur ve hata ilk istekte,
 * "undefined is not a function" seklinde ortaya cikar. Yon tek: depo bu
 * dosyayi import eder, bu dosya depoyu ETMEZ.
 */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

/* -------------------------------------------------------------------------- */
/* Depo ile arasindaki tek kopru                                               */
/* -------------------------------------------------------------------------- */

export type SessionHandlers = {
  /** Jetonlar donduruldu — depo kendi durumunu tazeleyebilir. */
  refreshed: () => void;
  /** Oturum kurtarilamadi — depo temizlenmeli. */
  lost: () => void;
};

let handlers: SessionHandlers | null = null;

/** Depo, modul yuklenirken kendini buraya kaydeder (bkz. session.store.ts). */
export const setSessionHandlers = (next: SessionHandlers | null): void => {
  handlers = next;
};

/* -------------------------------------------------------------------------- */
/* Sekmeler arasi haberlesme                                                   */
/* -------------------------------------------------------------------------- */

export const SESSION_CHANNEL_NAME = 'kural-session';

type SessionMessage =
  | { type: 'rotated'; accessToken: string }
  | { type: 'signed-out' };

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'function' ? new BroadcastChannel(SESSION_CHANNEL_NAME) : null;

const broadcast = (message: SessionMessage): void => {
  try {
    channel?.postMessage(message);
  } catch {
    /* kapali kanal / desteklenmeyen tarayici */
  }
};

/**
 * Diger sekmelere "cikis yapildi" duyurusu. Elle cikista (session.store signOut) da gerekli:
 * eskiden yalniz kurtarilamayan 401'de (loseSession) yayinlaniyordu, elle cikilan sekmenin
 * komsulari oturumu acik saniyordu.
 */
export const announceSignedOut = (): void => {
  broadcast({ type: 'signed-out' });
};

if (channel) {
  channel.onmessage = (event: MessageEvent<SessionMessage>) => {
    const message = event.data;
    if (message?.type === 'rotated') {
      // YENI ERISIM JETONU KOMSU SEKMEDEN GELIYOR — bunu almak SART.
      //
      // Sunucu donduruken eski oturumun erisim jetonlarini da iptal ediyor.
      // Bu sekme kendi (artik iptal edilmis) jetonuyla devam etseydi bir sonraki
      // istek 401 alir, kendi yenilemesini yapar, o da OTEKI sekmenin jetonunu
      // iptal ederdi: iki sekme birbirini sonsuza kadar yenilemeye zorlar.
      // Jeton ayni kokendeki sekmeler arasinda gidiyor ve hicbir yere yazilmiyor.
      accessToken = message.accessToken;
      return;
    }
    if (message?.type === 'signed-out') {
      accessToken = null;
      handlers?.lost();
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Yenileme                                                                    */
/* -------------------------------------------------------------------------- */

const REFRESH_URL = `${API_BASE_URL}${IAM_BASE}/v1/refresh`;

/**
 * Oturumu KURAN uclar. Bunlardan gelen 401 "kimlik bilgisi yanlis" demektir;
 * yenileyip tekrar denemek anlamsizdir.
 *
 * /v1/logout listede AYRI bir sebeple: yenileyip tekrar denemek YENI bir
 * yenileme jetonu basar, tekrar denenen istek ise govdedeki ESKI jetonu iptal
 * eder — oturum hayatta kalir, yani istenenin tam tersi olur.
 */
const SESSION_ENDPOINTS = ['/v1/login', '/v1/signup', '/v1/refresh', '/v1/logout'] as const;

const isSessionEndpoint = (url: string | undefined): boolean => {
  const path = String(url ?? '').split('?')[0] ?? '';
  return SESSION_ENDPOINTS.some((endpoint) => path.endsWith(endpoint));
};

/**
 * Yenileme cagrisi CIPLAK bir axios ornegiyle yapilir.
 *
 * `api` uzerinden gitseydi, yenilemenin kendisi 401 dondugunde asagidaki yanit
 * kesicisine yeniden girer ve kendini cagirirdi.
 */
const bareClient = axios.create({ timeout: 20_000 });

/** Ayni sekmedeki es zamanli 401'ler icin TEK ucus. */
let refreshInFlight: Promise<boolean> | null = null;

type RefreshResponse = {
  code?: string;
  accessToken?: string;
  refreshToken?: string;
  refreshExpiresAt?: number;
};

const performRefresh = async (): Promise<boolean> => {
  // JETON KILIDIN ICINDE, TAM BURADA OKUNUR.
  //
  // Kilidi beklerken baska bir sekme jetonu dondurmus olabilir. Kilit oncesinde
  // yakalanmis bir deger, tam olarak sunucunun "tekrar kullanim" saydigi sey
  // olur ve OTURUMU OLDURUR. Bu satirin yeri bir stil tercihi degil.
  const refreshToken = readRefreshToken();
  if (!refreshToken) return false;

  const deviceId = getDeviceId();
  try {
    const { data, headers } = await bareClient.post<RefreshResponse>(
      REFRESH_URL,
      { refreshToken, deviceId },
      { headers: { 'x-device-id': deviceId, 'Content-Type': 'application/json' } },
    );
    noteServerDate(headers['date'] as string | undefined);
    if (data?.code !== 'success' || !data.accessToken || !data.refreshToken) return false;

    accessToken = data.accessToken;
    writeRefreshToken(data.refreshToken, data.refreshExpiresAt ?? null);
    broadcast({ type: 'rotated', accessToken: data.accessToken });
    handlers?.refreshed();
    return true;
  } catch {
    return false;
  }
};

/** navigator.locks yoksa (Safari < 15.4, bazi gomulu tarayicilar) dogrudan calistirir. */
const withRefreshLock = <T>(run: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks || typeof locks.request !== 'function') return run();
  return locks.request('kural-refresh', run) as Promise<T>;
};

/**
 * Oturumu yeniler — SEKME ICINDE tek ucus, SEKMELER ARASI tek kilit.
 *
 * Ikisi de gerekli: modul degiskeni ayni sekmedeki es zamanli 401'leri
 * birlestirir, Web Locks farkli sekmeleri sıraya sokar. Biri eksik olsaydi
 * iki istek ayni yenileme jetonunu sunar; sunucu bunu tekrar kullanim sayar ve
 * oturumu komple dusurur. Uretimde oturum olduren hata tam olarak budur.
 *
 * @param tokenAtFailure 401 alan istegin tasidigi erisim jetonu. Kilit sirasinda
 *   jeton degistiyse (komsu sekme dondurdu) yenilemeye HIC gerek yoktur.
 */
export const refreshSession = (tokenAtFailure?: string | null): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = withRefreshLock(async () => {
      if (tokenAtFailure !== undefined && accessToken && accessToken !== tokenAtFailure) {
        return true;
      }
      return performRefresh();
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Kurtarilamayan 401: her seyi temizle ve TAM SAYFA giris ekranina git.
 *
 * Tam sayfa gezinme bilincli: react-query onbellegini, acik pencereleri ve
 * modul icindeki erisim jetonunu tek hamlede sifirlar. navigate() ile kalan
 * bir onbellek, sonraki kullaniciya onceki kullanicinin verisini gosterebilir.
 */
const loseSession = (): void => {
  accessToken = null;
  clearPersistedSession();
  broadcast({ type: 'signed-out' });
  handlers?.lost();
  hardNavigate('/giris');
};

/* -------------------------------------------------------------------------- */
/* Kesiciler                                                                   */
/* -------------------------------------------------------------------------- */

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  // HER istekte gonderilir, yalniz giriste degil: yenileme jetonu sunucuda bu
  // kimlige bagli ve /v1/refresh ikisini karsilastiriyor.
  config.headers.set('x-device-id', getDeviceId());
  return config;
});

api.interceptors.response.use(
  (response) => {
    noteServerDate(response.headers['date'] as string | undefined);
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    if (error.response) {
      noteServerDate(error.response.headers['date'] as string | undefined);
    }

    const original = error.config as RetryableConfig | undefined;
    const onSessionEndpoint = isSessionEndpoint(original?.url);

    if (status === 401 && original && !original._retriedAfterRefresh && !onSessionEndpoint) {
      // Bayrak ONCE yazilir: tekrar denenen istek yine 401 donerse dongu olusmaz.
      original._retriedAfterRefresh = true;
      const sentWith = original.headers?.get?.('Authorization');
      const tokenAtFailure =
        typeof sentWith === 'string' ? sentWith.replace(/^Bearer\s+/i, '') : null;

      if (await refreshSession(tokenAtFailure)) {
        // Ayni ornek uzerinden: istek kesicisi yeni jetonu kendisi takar.
        return api(original);
      }
    }

    if (status === 401 && !onSessionEndpoint) {
      loseSession();
    }

    throw error;
  },
);

/** YALNIZ TESTLER ICIN: modul ici durumu sifirlar. */
export const __resetClientStateForTests = (): void => {
  accessToken = null;
  refreshInFlight = null;
  handlers = null;
};
